import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

/**
 * How long typing pauses before the value is written into the ProseMirror
 * document (a transaction re-syncs the browser selection and would otherwise
 * bounce the caret to the end of the field on every keystroke).
 */
const COMMIT_DELAY_MS = 300;

type FieldElement = HTMLInputElement | HTMLTextAreaElement;

/**
 * Draft state for a node-view text field bound to a node attribute (ported from
 * Scilora `attr-fields.tsx`): the field renders its own draft, commits after a
 * pause or on blur, and restores the caret if the transaction moved it.
 */
function useAttrDraft(value: string, onCommit: (next: string) => void, ref: React.RefObject<FieldElement | null>) {
    const [draft, setDraft] = useState(value);
    const [focused, setFocused] = useState(false);
    const [lastValue, setLastValue] = useState(value);
    const commitRef = useRef(onCommit);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pending = useRef<string | null>(null);

    useEffect(() => {
        commitRef.current = onCommit;
    });

    if (!focused && value !== lastValue) {
        setLastValue(value);
        setDraft(value);
    }

    const flush = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
        const next = pending.current;
        pending.current = null;
        if (next === null) return;

        const field = ref.current;
        const hadFocus = !!field && document.activeElement === field;
        const start = field?.selectionStart ?? null;
        const end = field?.selectionEnd ?? null;

        commitRef.current(next);

        if (!hadFocus || start === null || end === null) return;
        requestAnimationFrame(() => {
            const el = ref.current;
            if (!el || document.activeElement !== el) return;
            if (el.selectionStart === start && el.selectionEnd === end) return;
            el.setSelectionRange(start, end);
        });
    }, [ref]);

    const handleChange = useCallback(
        (next: string) => {
            setDraft(next);
            pending.current = next;
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(flush, COMMIT_DELAY_MS);
        },
        [flush],
    );

    useEffect(
        () => () => {
            if (timer.current) clearTimeout(timer.current);
            if (pending.current === null) return;
            try {
                commitRef.current(pending.current);
            } catch {
                // Block is gone — nothing left to persist.
            }
        },
        [],
    );

    return { draft, handleChange, flush, setFocused };
}

type AttrTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
    value: string;
    onValueChange: (next: string) => void;
};

/**
 * MUI TextField for a block node view, bound to a node attribute. Drop-in for
 * `<TextField value={attr} onChange={…updateAttributes} />` that keeps the caret.
 */
export const AttrTextField: React.FC<AttrTextFieldProps> = ({ value, onValueChange, onFocus, onBlur, inputRef, ...rest }) => {
    const ref = useRef<FieldElement | null>(null);
    const { draft, handleChange, flush, setFocused } = useAttrDraft(value, onValueChange, ref);

    return (
        <TextField
            {...rest}
            inputRef={(el: FieldElement | null) => {
                ref.current = el;
                if (typeof inputRef === 'function') inputRef(el);
                else if (inputRef && 'current' in inputRef) (inputRef as React.MutableRefObject<FieldElement | null>).current = el;
            }}
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={(e) => {
                setFocused(true);
                onFocus?.(e);
            }}
            onBlur={(e) => {
                setFocused(false);
                flush();
                onBlur?.(e);
            }}
        />
    );
};

/** Stops ProseMirror from treating keystrokes/clicks inside form fields as editor input. */
export const stopEditorEvents = {
    onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
};
